import { SxProps, Theme } from '@mui/material';
import Box from '@mui/material/Box';
import { useContext, useEffect } from 'react';
import * as React from 'react';

import { ModalContext } from '@/components/modals/ModalContext';
import { Popper } from '@/components/poppers/Popper/Popper';
import { useDelayedCloseBehavior } from '@/hooks/useDelayedCloseBehavior';
import { makeUnprintableStyle } from '@/utils/makeUnprintableStyle';

import { HelpIconButton, HelpIconButtonProps } from './HelpIconButton';

type Props = React.PropsWithChildren<{
  sx?: SxProps<Theme>;
  wrappedComponent?: JSX.Element;
  // this is a prop that allows a developer to force the tooltip to stay open for debugging
  // and development
  _devOnlyOpen?: boolean;
  center?: boolean;
  // Forwarded to the default help icon (no effect when wrappedComponent is set),
  // e.g. to render a less prominent icon color.
  iconProps?: HelpIconButtonProps['iconProps'];
}>;

export function ContextualHelpTooltip({
  sx,
  children,
  wrappedComponent,
  _devOnlyOpen,
  center,
  iconProps,
}: Props) {
  const anchorEl = React.useRef(null);
  const { modalState } = useContext(ModalContext);

  const { isOpen, setIsMouseInTargetArea } = useDelayedCloseBehavior({
    isForceOpen: _devOnlyOpen,
  });

  useEffect(() => {
    // Close the tooltip whenever a modal opens. We depend on modalState (object reference)
    // rather than a derived boolean so the effect re-fires even when another modal is already open.
    const anyModalOpen = Object.values(modalState).some(
      (modal) => modal === true
    );
    if (anyModalOpen) {
      setIsMouseInTargetArea(false);
    }
  }, [modalState, setIsMouseInTargetArea]);

  // If we provide a width to the tooltip, we want to use that width instead of the width
  // of the wrapped component. This is useful when setting widths as a percentage of the
  // container element.
  const shouldAssumeWidthOfWrappedComponent = !(sx && 'width' in sx);
  const shouldAssumeHeightOfWrappedComponent = !(sx && 'height' in sx);

  return (
    <Box
      sx={makeUnprintableStyle({
        display: 'flex',
        alignItems: 'center',
        ...sx,
      })}
    >
      {wrappedComponent ? (
        <div
          onMouseEnter={() => setIsMouseInTargetArea(true)}
          onMouseLeave={() => setIsMouseInTargetArea(false)}
          ref={anchorEl}
          style={{
            width: shouldAssumeWidthOfWrappedComponent ? 'auto' : '100%',
            height: shouldAssumeHeightOfWrappedComponent ? 'auto' : '100%',
          }}
        >
          {wrappedComponent}
        </div>
      ) : (
        <HelpIconButton
          ref={anchorEl}
          buttonProps={{
            onMouseEnter: () => setIsMouseInTargetArea(true),
            onMouseLeave: () => setIsMouseInTargetArea(false),
            // The icon is focusable, so keyboard users get the same reveal as hover.
            onFocus: () => setIsMouseInTargetArea(true),
            onBlur: () => setIsMouseInTargetArea(false),
            sx: {
              cursor: 'default',
            },
          }}
          iconProps={iconProps}
        />
      )}
      {children && (
        <Popper
          anchorRef={anchorEl}
          onMouseEnter={() => setIsMouseInTargetArea(true)}
          onMouseLeave={() => setIsMouseInTargetArea(false)}
          open={isOpen}
          center={center}
        >
          {children}
        </Popper>
      )}
    </Box>
  );
}
